import { useCallback } from 'react';
import { Stack } from '@optimus-web/core';
import { TextKey } from '@mews-commander/enums';
import { DefaultErrorBoundary } from '@mews-commander/ui';
import { isCurrentEnterpriseRoomingListEnabled } from '@mews-commander/legacy-entities/src/selectors/session/subscription';
import { useCommanderSelector, useCommanderThunkDispatch } from '@mews-commander/utils';
import { WithScope } from '@mews-commander/types';
import { isSingleReservationChecked as isSingleReservationCheckedSelector } from '../../selectors';
import Warning from '../Warning';
import { RESERVATION_BARE_MINIMUM_EXTENT } from '../../constants';
import { fetchCurrentEnterpriseCheckedReservations, refetchReservationsByGroupId } from '../../actions';
import { SingleReservationCheckedComponents } from './SingleReservationCheckedComponents';
import { GroupNameForm } from './GroupNameForm/GroupNameForm';
import { GroupMergeForm } from './GroupMergeForm/GroupMergeForm';
import { ReservationMembershipForm } from './ReservationMembershipForm/ReservationMembershipForm';
import { UnassignedCompanionsListContainer } from './UnassignedCompanionsList/UnassignedCompanionsList';

const GroupTab = ({ scope }: WithScope) => {
    const isSingleReservationChecked = useCommanderSelector((state) =>
        isSingleReservationCheckedSelector(state, {
            scope,
        })
    );
    const isRoomingListEnabled = useCommanderSelector((state) => isCurrentEnterpriseRoomingListEnabled(state));
    const dispatch = useCommanderThunkDispatch();

    // ToDo: Remove this when the new reservation module is fully migrated and just invalidate queries inside submit form mutations
    const refetchReservationsGroup = useCallback(() => {
        dispatch(
            fetchCurrentEnterpriseCheckedReservations({
                scope,
                extent: { ...RESERVATION_BARE_MINIMUM_EXTENT, reservationGroups: true },
            })
        );
    }, [dispatch, scope]);

    // ToDo: Remove this when the new reservation module is fully migrated and just invalidate queries inside submit form mutations
    const refetchReservations = useCallback(() => {
        dispatch(refetchReservationsByGroupId({ groupId: scope }));
    }, [dispatch, scope]);

    // ToDo: Remove this when the new reservation module is fully migrated and just invalidate queries inside submit form mutations
    const refetchReservationsGroupCompanionships = useCallback(
        (withAccount = false) => {
            dispatch(
                fetchCurrentEnterpriseCheckedReservations({
                    scope,
                    extent: {
                        ...RESERVATION_BARE_MINIMUM_EXTENT,
                        accounts: withAccount,
                        companionships: true,
                    },
                })
            );
        },
        [dispatch, scope]
    );
    const refetchReservationsGroupCompanionshipsWithAccount = useCallback(() => {
        refetchReservationsGroupCompanionships(true);
    }, [refetchReservationsGroupCompanionships]);

    return (
        <DefaultErrorBoundary>
            <Stack spacing="200" vertical>
                <GroupNameForm scope={scope} onSuccess={refetchReservationsGroup} />
                <GroupMergeForm scope={scope} onSuccess={refetchReservations} />
                {isRoomingListEnabled ? (
                    <ReservationMembershipForm scope={scope} onSuccess={refetchReservations} />
                ) : null}
                {isSingleReservationChecked ? (
                    <>
                        <SingleReservationCheckedComponents
                            scope={scope}
                            onSuccess={refetchReservationsGroupCompanionshipsWithAccount}
                        />
                        <UnassignedCompanionsListContainer
                            scope={scope}
                            onSuccess={refetchReservationsGroupCompanionships}
                        />
                    </>
                ) : (
                    <Warning textKey={TextKey.MoreOptionsOneReservation} />
                )}
            </Stack>
        </DefaultErrorBoundary>
    );
};

export default GroupTab;
